import { useState, useEffect } from 'react'

// Shared plumbing for the Tracker views — spinner, debounced search,
// pagination footer, and the scrollable table shell with a working
// sticky header (sticky must live on <th> inside a bounded-height
// scroll container, or it silently doesn't stick).

export function Spinner({ className = 'h-5 w-5 text-brand-600' }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}

export function LoadingRow({ colSpan }) {
  return (
    <tr><td colSpan={colSpan} className="px-3 py-12">
      <div className="flex items-center justify-center gap-2 text-gray-400 text-sm"><Spinner className="h-4 w-4 text-gray-400" /> Loading…</div>
    </td></tr>
  )
}

export function EmptyRow({ colSpan, children }) {
  return <tr><td colSpan={colSpan} className="px-3 py-12 text-center text-sm text-gray-400">{children}</td></tr>
}

// 350ms debounced search input state: [input, setInput, debouncedValue]
export function useDebouncedSearch(onChange) {
  const [input, setInput] = useState('')
  const [value, setValue] = useState('')
  useEffect(() => {
    const t = setTimeout(() => { setValue(input); onChange?.() }, 350)
    return () => clearTimeout(t)
  }, [input]) // eslint-disable-line react-hooks/exhaustive-deps
  return [input, setInput, value]
}

export function PaginationFooter({ page, totalPages, total, label, onPage }) {
  return (
    <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
      <span>{label}</span>
      <div className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Prev</button>
        <span>Page {page} / {totalPages || 1}</span>
        <button disabled={page >= (totalPages || 1)} onClick={() => onPage(page + 1)} className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next</button>
      </div>
    </div>
  )
}

// Scrollable table shell: bounded height so the header actually sticks.
export function TableShell({ children, maxH = 'max-h-[70vh]' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-auto ${maxH}`}>
      {children}
    </div>
  )
}

// Sticky header cell — bg + z-index on the th itself.
export function Th({ children, className = '', onClick, sorted, align = 'left', ...rest }) {
  return (
    <th
      onClick={onClick}
      className={[
        'sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-2.5 py-2 font-medium whitespace-nowrap uppercase tracking-wide text-gray-500',
        align === 'right' ? 'text-right' : 'text-left',
        onClick ? 'cursor-pointer select-none hover:text-gray-700' : '',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}{sorted && <span className="ml-1 text-brand-600">{sorted === 'asc' ? '▲' : '▼'}</span>}
    </th>
  )
}

// Keyboard-accessible clickable row props.
export function rowClickProps(onOpen) {
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } },
  }
}

// A clock that ticks, for anything whose output is a function of elapsed time.
//
// Deliberately a hook used by LEAF components rather than state in a parent:
// held in TrackerTab it re-rendered the whole 52-card board once a minute for
// the sake of one line of text. `TrackerBoard` is not memoised, so that cost is
// real and entirely avoidable. Cleared on unmount; the Tracker tab unmounts
// when another portal tab is selected, so nothing ticks in the background.
//
// Also re-reads on focus/visibility: the case this exists for is a board left
// open overnight, and the moment that matters is someone coming back to it.
export function useTickingClock(ms = 60000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const tick = () => setNow(Date.now())
    const id = setInterval(tick, ms)
    const onVisible = () => { if (!document.hidden) tick() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', tick)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', tick)
    }
  }, [ms])
  return now
}
